import {
  Anchor,
  Badge,
  Card,
  Group,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  describeLlmPurpose,
  LLM_PURPOSE_GROUP_META,
  type AdminCostsResponse,
  type LlmPurposeGroup,
} from '@prepzio/shared';

import { InfoTip } from '../components/InfoTip';
import { useCosts } from '../hooks/use-content';
import { useI18n, useT, type MessageKey, type TranslateFn } from '../i18n';
import { errorMessage } from '../lib/ui';

/** USD with 4 decimals for tiny per-call amounts, commas for the big ones. */
function usd(n: number, locale: string): string {
  return `$${n.toLocaleString(locale, { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
}
/**
 * A group's share of the window, or an em dash when there is nothing to divide.
 *
 * Formatted through `Intl` rather than by appending "%": Turkish writes the sign
 * before the number ("%50"), and a hand-built string would print "50%" in a
 * Turkish panel.
 */
function shareOf(part: number, total: number, locale: string): string {
  if (!(total > 0)) return '—';
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
    part / total,
  );
}

function num(n: number, locale: string): string {
  return n.toLocaleString(locale);
}

/** A ledger call status, in words. Unknown states print themselves. */
function statusLabel(status: string, t: TranslateFn): string {
  const key = `costs.status.${status}` as MessageKey;
  const translated = t(key);
  return translated === key ? status : translated;
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card withBorder padding="md" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700} fz={24} mt={2}>
        {value}
      </Text>
      {sub && (
        <Text size="xs" c="dimmed">
          {sub}
        </Text>
      )}
    </Card>
  );
}

/**
 * A purpose value with a tooltip explaining which LLM call it is and where it
 * fires (PRE-197). Descriptions live in one place — `LLM_PURPOSE_META`
 * (@prepzio/shared) — and an undescribed purpose renders a visible warning
 * rather than a bare code, so a new ledger purpose can't slip in unexplained.
 * Tooltip opens on hover, keyboard focus, and touch (mobile-friendly).
 */
function PurposeBadge({ purpose }: { purpose: string }) {
  const t = useT();
  const meta = describeLlmPurpose(purpose);
  const label = meta ? (
    <>
      <Text span fw={600}>
        {meta.label}
      </Text>
      {' — '}
      {meta.description}
    </>
  ) : (
    t('costs.noPurposeDescription')
  );
  return (
    <Tooltip
      label={label}
      multiline
      w={280}
      withArrow
      events={{ hover: true, focus: true, touch: true }}
    >
      <Badge
        variant="light"
        color={meta ? 'gray' : 'orange'}
        tt="none"
        tabIndex={0}
        style={{ cursor: 'help' }}
      >
        {meta ? purpose : `⚠ ${purpose}`}
      </Badge>
    </Tooltip>
  );
}

/** A tiny inline bar chart of per-day spend (no chart lib needed). */
function DayBars({ byDay }: { byDay: AdminCostsResponse['byDay'] }) {
  const { t, locale } = useI18n();
  if (byDay.length === 0) return null;
  const max = Math.max(...byDay.map((d) => d.costUsd), 0.000001);
  return (
    <Group gap={3} align="flex-end" wrap="nowrap" style={{ height: 90, overflowX: 'auto' }}>
      {byDay.map((d) => (
        <div
          key={d.day}
          style={{ flex: '0 0 10px', textAlign: 'center' }}
          title={t('costs.dayTooltip', {
            day: d.day,
            cost: usd(d.costUsd, locale),
            calls: d.calls,
          })}
        >
          <div
            style={{
              height: Math.max(2, Math.round((d.costUsd / max) * 80)),
              background: 'var(--mantine-color-brand-5)',
              borderRadius: 2,
            }}
          />
        </div>
      ))}
    </Group>
  );
}

export function CostsScreen() {
  const { t, locale } = useI18n();
  const { data, isLoading, isError, error } = useCosts();

  if (isError)
    return (
      <Text c="red" size="sm">
        {errorMessage(error)}
      </Text>
    );
  if (isLoading || !data)
    return (
      <Text c="dimmed" size="sm">
        {t('common.loading')}
      </Text>
    );

  const capPct =
    data.monthlyCapUsd && data.monthlyCapUsd > 0
      ? Math.min(100, (data.monthToDateUsd / data.monthlyCapUsd) * 100)
      : null;

  return (
    <Stack gap="md">
      <Group gap={6} align="center">
        <Text c="dimmed" size="sm">
          {t('costs.intro')}
        </Text>
        <InfoTip label={t('costs.infoTip')} />
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Stat
          label={t('costs.allTime')}
          value={usd(data.totals.costUsd, locale)}
          sub={t('costs.callsSub', { count: num(data.totals.calls, locale) })}
        />
        <Stat
          label={t('costs.lastDays', { days: data.window.days })}
          value={usd(data.window.costUsd, locale)}
          sub={t('costs.callsSub', { count: num(data.window.calls, locale) })}
        />
        <Stat label={t('costs.monthToDate')} value={usd(data.monthToDateUsd, locale)} />
        <Stat
          label={t('costs.tokensAllTime')}
          value={num(data.totals.inputTokens + data.totals.outputTokens, locale)}
          sub={t('costs.tokensSub', {
            in: num(data.totals.inputTokens, locale),
            out: num(data.totals.outputTokens, locale),
          })}
        />
      </SimpleGrid>

      {data.monthlyCapUsd != null && (
        <Card withBorder padding="md" radius="md">
          <Group justify="space-between" mb={6}>
            <Text size="sm" fw={600}>
              {t('costs.budget')}
            </Text>
            <Text size="sm" c="dimmed">
              {usd(data.monthToDateUsd, locale)} / {usd(data.monthlyCapUsd, locale)}
            </Text>
          </Group>
          {capPct != null && (
            <Progress
              value={capPct}
              color={capPct > 90 ? 'red' : capPct > 70 ? 'yellow' : 'brand'}
            />
          )}
          <Text size="xs" c="dimmed" mt={4}>
            {t('costs.budgetNote')}
          </Text>
        </Card>
      )}

      {/*
        PRE-131: the roll-up above purpose.
        Placed ABOVE "Spend by purpose" on purpose. Eleven purposes in one list
        made the two biggest numbers adjacent and therefore comparable, which
        they are not: one grows with learners, the other with how often we point
        our own instruments at the repository. The split says that first.
      */}
      <Card withBorder padding="lg" radius="md">
        <Group gap={6} mb={4}>
          <Text fw={600}>{t('costs.byArea')}</Text>
          <InfoTip label={t('costs.byAreaTip')} />
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {t('costs.learnerFacing')}
            </Text>
            <Text fw={700} size="xl">
              {usd(data.split.learnerFacingUsd, locale)}
            </Text>
            <Text size="xs" c="dimmed">
              {t('costs.learnerFacingSub', {
                share: shareOf(data.split.learnerFacingUsd, data.window.costUsd, locale),
              })}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              {t('costs.ours')}
            </Text>
            <Text fw={700} size="xl">
              {usd(data.split.internalUsd, locale)}
            </Text>
            <Text size="xs" c="dimmed">
              {t('costs.oursSub', {
                share: shareOf(data.split.internalUsd, data.window.costUsd, locale),
              })}
            </Text>
          </div>
        </SimpleGrid>

        <Table.ScrollContainer minWidth={560}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('costs.colArea')}</Table.Th>
                <Table.Th ta="right">{t('costs.colCost')}</Table.Th>
                <Table.Th ta="right">{t('costs.colShare')}</Table.Th>
                <Table.Th ta="right">{t('costs.colCalls')}</Table.Th>
                <Table.Th>{t('costs.colPurposes')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.byGroup.map((g) => {
                const meta = LLM_PURPOSE_GROUP_META[g.group as LlmPurposeGroup];
                return (
                  <Table.Tr key={g.group}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Badge
                          variant="light"
                          color={
                            g.learnerFacing ? 'teal' : g.group === 'retired' ? 'gray' : 'grape'
                          }
                          tt="none"
                        >
                          {g.label}
                        </Badge>
                        {meta && <InfoTip label={meta.description} />}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">{usd(g.costUsd, locale)}</Table.Td>
                    <Table.Td ta="right">
                      {shareOf(g.costUsd, data.window.costUsd, locale)}
                    </Table.Td>
                    <Table.Td ta="right">{num(g.calls, locale)}</Table.Td>
                    <Table.Td>
                      <Text size="xs" c="dimmed">
                        {g.purposes.join(' · ')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {data.byGroup.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">
                      {t('costs.noSpend')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Text fw={600} mb="sm">
          {t('costs.byPurpose')}
        </Text>
        <Table.ScrollContainer minWidth={560}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('costs.colPurpose')}</Table.Th>
                <Table.Th ta="right">{t('costs.colCost')}</Table.Th>
                <Table.Th ta="right">{t('costs.colCalls')}</Table.Th>
                <Table.Th ta="right">{t('costs.colInTokens')}</Table.Th>
                <Table.Th ta="right">{t('costs.colOutTokens')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.byPurpose.map((r) => (
                <Table.Tr key={r.purpose}>
                  <Table.Td>
                    <PurposeBadge purpose={r.purpose} />
                  </Table.Td>
                  <Table.Td ta="right">{usd(r.costUsd, locale)}</Table.Td>
                  <Table.Td ta="right">{num(r.calls, locale)}</Table.Td>
                  <Table.Td ta="right">{num(r.inputTokens, locale)}</Table.Td>
                  <Table.Td ta="right">{num(r.outputTokens, locale)}</Table.Td>
                </Table.Tr>
              ))}
              {data.byPurpose.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={5}>
                    <Text c="dimmed" size="sm">
                      {t('costs.noCalls')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder padding="lg" radius="md">
          <Text fw={600} mb="sm">
            {t('costs.byModel')}
          </Text>
          <Table.ScrollContainer minWidth={360}>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('costs.colModel')}</Table.Th>
                  <Table.Th ta="right">{t('costs.colCost')}</Table.Th>
                  <Table.Th ta="right">{t('costs.colCalls')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {data.byModel.map((r) => (
                  <Table.Tr key={`${r.provider}/${r.model}`}>
                    <Table.Td>
                      <Text size="sm" style={{ wordBreak: 'break-all' }}>
                        {r.model}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {r.provider}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{usd(r.costUsd, locale)}</Table.Td>
                    <Table.Td ta="right">{num(r.calls, locale)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>

        <Card withBorder padding="lg" radius="md">
          <Text fw={600} mb="sm">
            {t('costs.byStatus')}
          </Text>
          <Stack gap="xs">
            {data.byStatus.map((r) => (
              <Group key={r.status} justify="space-between">
                <Badge
                  variant="light"
                  color={r.status === 'ok' ? 'green' : r.status === 'error' ? 'red' : 'yellow'}
                  tt="none"
                >
                  {statusLabel(r.status, t)}
                </Badge>
                <Text size="sm">
                  {t('costs.statusLine', {
                    count: num(r.calls, locale),
                    cost: usd(r.costUsd, locale),
                  })}
                </Text>
              </Group>
            ))}
            {data.byStatus.length === 0 && (
              <Text c="dimmed" size="sm">
                —
              </Text>
            )}
          </Stack>
          <Text fw={600} mt="lg" mb="xs">
            {t('costs.daily', { days: data.window.days })}
          </Text>
          <DayBars byDay={data.byDay} />
        </Card>
      </SimpleGrid>

      <Card withBorder padding="lg" radius="md">
        <Group gap={6} align="center" mb="sm">
          <Text fw={600}>{t('costs.recent')}</Text>
          <InfoTip label={t('costs.recentTip')} />
        </Group>
        <Table.ScrollContainer minWidth={720}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('costs.colWhen')}</Table.Th>
                <Table.Th>{t('costs.colPurpose')}</Table.Th>
                <Table.Th>{t('common.status')}</Table.Th>
                <Table.Th>{t('costs.colModel')}</Table.Th>
                <Table.Th ta="right">{t('costs.colCost')}</Table.Th>
                <Table.Th ta="right">{t('costs.colLatency')}</Table.Th>
                <Table.Th>{t('costs.colTrace')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.recentCalls.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Text size="sm">{r.createdAt}</Text>
                  </Table.Td>
                  <Table.Td>
                    <PurposeBadge purpose={r.purpose} />
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={r.status === 'ok' ? 'green' : r.status === 'error' ? 'red' : 'yellow'}
                      tt="none"
                    >
                      {statusLabel(r.status, t)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" style={{ wordBreak: 'break-all' }}>
                      {r.model}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {r.provider}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">{usd(r.costUsd, locale)}</Table.Td>
                  <Table.Td ta="right">{num(r.latencyMs, locale)} ms</Table.Td>
                  <Table.Td>
                    {r.llmTraceUrl ? (
                      <Anchor href={r.llmTraceUrl} target="_blank" rel="noreferrer" size="sm">
                        {t('costs.llmTrace')}
                      </Anchor>
                    ) : (
                      <Text c="dimmed" size="sm">
                        —
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
              {data.recentCalls.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" size="sm">
                      {t('costs.noCalls')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>
    </Stack>
  );
}
